import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import client from "../../utils/api";
import { createApiAction } from "../../utils/redux-tool";

const name = "application-enrollment";
const initialState = {
  enrollment: null,
  enrollmentLoading: false,
  enrollmentError: null,
  documentList: null,
  documentLoading: false,
  enrollmentSummaryList: null,
  enrollmentSummaryLoading: false,
};

export const getEnrollmentData = createApiAction(
  "application/enrollment/get",
  client.retrieve(`/api/application/enrollment/:id`)
);
export const updateEnrollmentData = createApiAction(
  "application/enrollment/update",
  client.save(`/api/application/enrollment`, "PATCH")
);

export const startEnrollmentProcess = createAsyncThunk(
  "application/enrollment/start",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await client.post('/api/application/enrollment', payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const documentSave = createApiAction("application/enrollment/document/save", client.save('/api/application/enrollment/document'));
export const documentGet = createApiAction("application/enrollment/document/get", client.retrieve('/api/application/enrollment/document'));
export const documentDestroy = createApiAction("application/enrollment/document/destroy", client.destroy('/api/application/enrollment/document/:id'));

export const fileSave = createApiAction("application/enrollment/file/save", client.save('/api/application/enrollment/document/file'));

export const communicationSave = createApiAction("application/enrollment/communication/save", client.save('/api/application/enrollment/document/communication'));
export const communicationGet = createApiAction("application/enrollment/communication/get", client.retrieve('/api/application/enrollment/document/communication/:id'));

export const sendNotification = createApiAction("application/enrollment/notification/send", client.save('/api/application/enrollment/notification'));

export const searchEnrollmentSummary = createApiAction("application/enrollment/summary/search", client.search('/api/application/enrollment/summary'));


const sessionSlice = createSlice({
  name,
  initialState,
  reducers: {
    clearEnrollmentState(state) {
      // Clear data; avoid toggling flags in a way that fights pending reducers.
      state.enrollment = null;
      state.enrollmentError = null;
      state.documentList = null;
      state.documentLoading = false;
      state.enrollmentSummaryList = null;
      // leave state.enrollmentLoading as-is; getEnrollmentData.pending will set it correctly
    },
  },
  extraReducers(builder) {
    builder

      .addCase(getEnrollmentData.pending, (state) => {
        state.enrollmentLoading = true;
        state.enrollmentError = null;
        state.enrollment = null;
      })
      .addCase(getEnrollmentData.fulfilled, (state, action) => {
        state.enrollmentLoading = false;
        state.enrollment = action.payload;
      })
      .addCase(getEnrollmentData.rejected, (state, action) => {
        state.enrollmentLoading = false;
        state.enrollment = null;
        state.enrollmentError =
          action.payload || action.error?.message || "Request failed.";
      })
      .addCase(updateEnrollmentData.pending, (state) => {
        state.enrollmentLoading = true;
        state.enrollmentError = null;
      })
      .addCase(updateEnrollmentData.fulfilled, (state, action) => {
        state.enrollmentLoading = false;
        state.enrollment = action.payload;
      })
      .addCase(updateEnrollmentData.rejected, (state, action) => {
        state.enrollmentLoading = false;
        state.enrollmentError =
          action.payload || action.error?.message || "Update failed.";
      })
      .addCase(sendNotification.fulfilled, (state, action) => {
        if (state.enrollment && action.payload?.lastNotificationDate) {
          state.enrollment.lastNotificationDate = action.payload.lastNotificationDate;
          state.enrollment.extendDate = action.payload.extendDate;
          state.enrollment.enrollmentPackageView = action.payload.enrollmentPackageView;
        }
      })
      .addCase(searchEnrollmentSummary.pending, (state) => {
        state.enrollmentSummaryList = null;
        state.enrollmentSummaryLoading = true;
      })
      .addCase(searchEnrollmentSummary.fulfilled, (state, action) => {
        state.enrollmentSummaryList = action.payload;
        state.enrollmentSummaryLoading = false;
      })

  },
});

export const { clearEnrollmentState } = sessionSlice.actions;
export default sessionSlice.reducer;
